import type { Ayah, TafsirLanguage } from '@/types/domain';
import { HighlightAllahText } from '@/components/ui/HighlightAllahText';
import { cn } from '@/lib/utils/cn';

type TafsirPanelProps = {
  ayah: Pick<Ayah, 'tafsir_en' | 'tafsir_ur'>;
  language: TafsirLanguage;
  className?: string;
};

/**
 * Phase 1 placeholder.
 *
 * Renders raw tafsir text from `ayahs.tafsir_en` / `ayahs.tafsir_ur` if
 * present. No formatting, footnote parsing, or scholarly framing yet —
 * that work belongs to a later phase along with translator/tafsir
 * attribution (the schema does not carry attribution columns; see
 * DATABASE_MAP.md §9 item 4).
 */
export function TafsirPanel({ ayah, language, className }: TafsirPanelProps) {
  const text = language === 'ur' ? ayah.tafsir_ur : ayah.tafsir_en;
  if (!text) return null;

  const isRtl = language === 'ur';

  return (
    <aside
      className={cn(
        'mt-6 pt-5 border-t border-dashed border-ink-200/70 dark:border-ink-800/70',
        className,
      )}
    >
      <p className="text-[10px] uppercase tracking-[0.18em] text-ink-400 dark:text-ink-500 mb-2">
        Tafsir · {language === 'ur' ? 'Urdu' : 'English'}
      </p>
      <div
        lang={language}
        dir={isRtl ? 'rtl' : 'ltr'}
        className={cn(
          'text-sm leading-relaxed text-ink-600 dark:text-ink-300',
          // Urdu tafsir must use Noto Nastaliq Urdu via .urdu-display;
          // applying font-arabic here was a bug (rendered as Amiri Quran).
          isRtl && 'urdu-display',
        )}
      >
        <HighlightAllahText text={text} />
      </div>
    </aside>
  );
}
